<script setup>
import { ref, computed } from 'vue';
import {
  addDays,
  addHours,
  getUnixTime,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
} from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CustomSnoozeModal from 'dashboard/components/CustomSnoozeModal.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const DV_SNOOZE_OPTIONS = {
  IN_ONE_HOUR: 'dv_in_one_hour',
  IN_THREE_HOURS: 'dv_in_three_hours',
  THIS_EVENING: 'dv_this_evening_18',
  TOMORROW_MORNING: 'dv_tomorrow_morning_08',
  TOMORROW_AFTERNOON: 'dv_tomorrow_afternoon_15',
  CUSTOM: wootConstants.SNOOZE_OPTIONS.UNTIL_CUSTOM_TIME,
};

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const statusButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

const [showStatusDropdown, toggleStatusDropdown] = useToggle();
const [showSnoozeDropdown, toggleSnoozeDropdown] = useToggle();
const [showCustomTimeSnoozeModal, toggleCustomTimeSnoozeModal] = useToggle();

const closeStatusDropdown = () => toggleStatusDropdown(false);
const closeSnoozeDropdown = () => toggleSnoozeDropdown(false);
const closeAllDropdowns = () => {
  closeStatusDropdown();
  closeSnoozeDropdown();
};

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showPendingAction = computed(
  () => !isPending.value && !isSnoozed.value
);
const showSnoozeAction = computed(
  () => !isPending.value && !isSnoozed.value
);
const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const snoozeOptions = computed(() => [
  {
    key: DV_SNOOZE_OPTIONS.IN_ONE_HOUR,
    label: 'Через час',
    icon: 'i-lucide-clock-3',
  },
  {
    key: DV_SNOOZE_OPTIONS.IN_THREE_HOURS,
    label: 'Через 3 часа',
    icon: 'i-lucide-clock-3',
  },
  {
    key: DV_SNOOZE_OPTIONS.THIS_EVENING,
    label: 'Вечером в 18 часов',
    icon: 'i-lucide-sunset',
  },
  {
    key: DV_SNOOZE_OPTIONS.TOMORROW_MORNING,
    label: 'Завтра утром в 8 часов',
    icon: 'i-lucide-sunrise',
  },
  {
    key: DV_SNOOZE_OPTIONS.TOMORROW_AFTERNOON,
    label: 'Завтра днем в 15 часов',
    icon: 'i-lucide-calendar-clock',
  },
  {
    key: DV_SNOOZE_OPTIONS.CUSTOM,
    label: 'Custom',
    icon: 'i-lucide-calendar-clock',
  },
]);

const atLocalTime = (date, hour) =>
  setMilliseconds(setSeconds(setMinutes(setHours(date, hour), 0), 0), 0);

const findDvSnoozeTime = snoozeType => {
  const now = new Date();

  if (snoozeType === DV_SNOOZE_OPTIONS.IN_ONE_HOUR) {
    return getUnixTime(addHours(now, 1));
  }

  if (snoozeType === DV_SNOOZE_OPTIONS.IN_THREE_HOURS) {
    return getUnixTime(addHours(now, 3));
  }

  if (snoozeType === DV_SNOOZE_OPTIONS.THIS_EVENING) {
    const todayAtSix = atLocalTime(now, 18);
    return getUnixTime(todayAtSix > now ? todayAtSix : addDays(todayAtSix, 1));
  }

  if (snoozeType === DV_SNOOZE_OPTIONS.TOMORROW_MORNING) {
    return getUnixTime(atLocalTime(addDays(now, 1), 8));
  }

  if (snoozeType === DV_SNOOZE_OPTIONS.TOMORROW_AFTERNOON) {
    return getUnixTime(atLocalTime(addDays(now, 1), 15));
  }

  return null;
};

const getConversationParams = () => {
  const conversationList = document.querySelector('.conversations-list');
  const allConversations = conversationList
    ? [...conversationList.querySelectorAll('.conversation')].filter(
        conversation => conversation.offsetParent !== null
      )
    : [];
  const activeConversation = conversationList?.querySelector('.conversation.active');
  const activeConversationIndex = allConversations.indexOf(activeConversation);
  const lastConversationIndex = allConversations.length - 1;

  return {
    list: conversationList,
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const hasUnreadMessages = conversationElement => {
  const unreadBadge = conversationElement?.querySelector('.bg-n-teal-9');

  return !!(
    unreadBadge &&
    !unreadBadge.classList.contains('hidden') &&
    unreadBadge.textContent?.trim()
  );
};

const getNextConversationElement = ({ preferUnread = true } = {}) => {
  const { all, activeIndex } = getConversationParams();

  if (!all.length) {
    return null;
  }

  const nextConversations =
    activeIndex >= 0 ? all.slice(activeIndex + 1) : [...all];
  const previousConversations =
    activeIndex > 0 ? all.slice(0, activeIndex) : [];
  const candidates = [...nextConversations, ...previousConversations].filter(
    conversation => !conversation.classList.contains('active')
  );

  if (!candidates.length) {
    return null;
  }

  if (preferUnread) {
    const unreadConversation = candidates.find(hasUnreadMessages);
    if (unreadConversation) {
      return unreadConversation;
    }
  }

  return candidates[0];
};

const advanceToNextConversation = nextConversationElement => {
  window.requestAnimationFrame(() => {
    window.setTimeout(() => {
      if (nextConversationElement?.isConnected) {
        nextConversationElement.click();
        return;
      }

      const fallbackConversation = getNextConversationElement({
        preferUnread: true,
      });

      fallbackConversation?.click();
    }, 80);
  });
};

const toggleStatus = (
  status,
  snoozedUntil,
  customAttributes = null,
  { advanceToNext = false, nextConversationElement = null } = {}
) => {
  closeAllDropdowns();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store
    .dispatch('toggleStatus', payload)
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));

      if (advanceToNext && status === wootConstants.STATUS_TYPE.RESOLVED) {
        advanceToNextConversation(nextConversationElement);
      }
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes,
      {
        advanceToNext: !!context.advanceToNext,
        nextConversationElement: context.nextConversationElement || null,
      }
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = ({ advanceToNext = false } = {}) => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );
  const nextConversationElement = advanceToNext
    ? getNextConversationElement({ preferUnread: true })
    : null;

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
      advanceToNext,
      nextConversationElement,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED, null, null, {
      advanceToNext,
      nextConversationElement,
    });
  }
};

const onSelectSnoozeOption = snoozeType => {
  if (snoozeType === DV_SNOOZE_OPTIONS.CUSTOM) {
    closeSnoozeDropdown();
    toggleCustomTimeSnoozeModal(true);
    return;
  }

  toggleStatus(
    wootConstants.STATUS_TYPE.SNOOZED,
    findDvSnoozeTime(snoozeType) || findSnoozeTime(snoozeType) || null
  );
};

const customSnoozeTime = customSnoozedTime => {
  toggleCustomTimeSnoozeModal(false);
  if (customSnoozedTime) {
    toggleStatus(
      wootConstants.STATUS_TYPE.SNOOZED,
      getUnixTime(customSnoozedTime)
    );
  }
};

const hideCustomSnoozeModal = () => {
  toggleCustomTimeSnoozeModal(false);
};

const openStatusMenu = () => {
  closeSnoozeDropdown();
  toggleStatusDropdown(true);
};

const toggleSnoozeMenu = () => {
  closeStatusDropdown();
  toggleSnoozeDropdown();
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => statusButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { list, all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation({ advanceToNext: true });

      if (activeIndex === lastIndex && all.length > 1 && list) {
        list.scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center gap-2 resolve-actions">
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        icon="i-lucide-check-check"
        size="sm"
        color="slate"
        no-animation
        class="resolve-actions__main ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="() => onCmdResolveConversation({ advanceToNext: true })"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        icon="i-lucide-rotate-ccw"
        size="sm"
        color="slate"
        no-animation
        class="resolve-actions__main ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        icon="i-lucide-inbox"
        size="sm"
        color="slate"
        no-animation
        class="resolve-actions__main"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showPendingAction"
        ref="statusButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openStatusMenu"
      />
    </ButtonGroup>

    <div
      v-if="showPendingAction && showStatusDropdown"
      v-on-clickaway="closeStatusDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-50 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-[3.75rem] max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>

    <div
      v-if="showSnoozeAction"
      v-on-clickaway="closeSnoozeDropdown"
      class="relative flex items-center"
    >
      <Button
        v-tooltip="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
        icon="i-lucide-alarm-clock"
        size="sm"
        color="slate"
        outline
        class="resolve-actions__snooze !px-0"
        :disabled="isLoading"
        @click="toggleSnoozeMenu"
      />
      <div
        v-if="showSnoozeDropdown"
        class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 z-50 bg-n-alpha-3 backdrop-blur-[100px] absolute block top-full mt-0.5 right-0 min-w-[14rem] [&_ul>li]:mb-0"
      >
        <WootDropdownMenu class="mb-0">
          <WootDropdownItem
            v-for="option in snoozeOptions"
            :key="option.key"
          >
            <Button
              :label="option.label"
              ghost
              slate
              sm
              start
              :icon="option.icon"
              class="w-full"
              @click="() => onSelectSnoozeOption(option.key)"
            />
          </WootDropdownItem>
        </WootDropdownMenu>
      </div>
    </div>

    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <woot-modal
      v-model:show="showCustomTimeSnoozeModal"
      :on-close="hideCustomSnoozeModal"
    >
      <CustomSnoozeModal
        @close="hideCustomSnoozeModal"
        @choose-time="customSnoozeTime"
      />
    </woot-modal>
  </div>
</template>

<style scoped>
.resolve-actions__main {
  min-width: 8.75rem;
}

.resolve-actions__snooze {
  width: 2.5rem;
  min-width: 2.5rem;
}

@media (max-width: 1279px) {
  .resolve-actions {
    gap: 0.375rem;
  }

  .resolve-actions__main {
    width: 2.5rem;
    min-width: 2.5rem;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .resolve-actions__main :deep(span.min-w-0.truncate) {
    display: none;
  }
}
</style>
